import React from "react";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent } from "@shared/ui/card";
import { Check, ClipboardList, AlertCircle, HelpCircle } from "lucide-react";
import { SubtypeDetail } from "../Detail";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

interface OverviewTabProps {
  detail: SubtypeDetail;
}

export default function OverviewTab({ detail }: OverviewTabProps) {
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardContent className="p-6 space-y-8">
        {/* Field Summary */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-800">
            Field summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <QuickStatsCard
              title="Total fields"
              count={detail.customFields.length}
              icon={
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                  <ClipboardList className="w-5 h-5" />
                </div>
              }
              className="border-gray-200 shadow-sm"
            />
            <QuickStatsCard
              title="Required"
              count={detail.customFields.filter((f) => f.required).length}
              icon={
                <div className="p-2 bg-red-50 text-red-600 rounded-lg">
                  <AlertCircle className="w-5 h-5" />
                </div>
              }
              className="border-gray-200 shadow-sm"
            />
            <QuickStatsCard
              title="Optional"
              count={detail.customFields.filter((f) => !f.required).length}
              icon={
                <div className="p-2 bg-slate-50 text-slate-600 rounded-lg">
                  <HelpCircle className="w-5 h-5" />
                </div>
              }
              className="border-gray-200 shadow-sm"
            />
          </div>
        </div>

        {/* Inherited Capabilities */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-800">
            Inherited capabilities <span className="text-xs font-normal text-gray-500 lowercase normal-case">— from {detail.resourceType} resource type</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {Object.entries(detail.capabilities).map(([key, val]) => (
              <Card 
                key={key} 
                className="border-gray-200 shadow-sm hover:border-blue-200 hover:bg-blue-50/5 transition-all duration-150"
              >
                <CardContent className="p-4 flex flex-col justify-between h-24">
                  <span className="text-xs font-medium text-gray-500">
                    {key.replace(/([A-Z])/g, " $1").toLowerCase()}
                  </span>
                  {val ? (
                    <div className="flex items-center gap-1.5 text-green-600 font-medium">
                      <span className="p-0.5 bg-green-50 text-green-600 rounded-full">
                        <Check className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-xs font-semibold text-green-700">Supported</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-gray-400 font-medium">
                      <span className="w-4 h-4 rounded-full border-2 border-gray-300 block" />
                      <span className="text-xs font-semibold text-gray-400">Disabled</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Supported Activities & Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
          <ValueLabel
            label="Supported activities"
            value={
              <div className="flex flex-wrap gap-2 mt-1">
                {detail.activities.map((activity, idx) => (
                  <Badge key={idx} variant="outline" className="text-blue-600 bg-blue-50 border-blue-100">
                    {activity}
                  </Badge>
                ))}
              </div>
            }
          />
          <ValueLabel
            label="Resource type status"
            value={
              <div className="mt-1">
                <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200">
                  {detail.status}
                </Badge>
              </div>
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}
